<template>
  <b-container>
    <div v-if="missingData.length > 0 && hasAnyOfTheseRoles(['admin', 'supply_agent'])" class="row alert alert-danger">
      <b-col cols="3">
        <b-button variant="danger"
          data-toggle="collapse"
          data-target="#missing-data"
          aria-expanded="false"
          aria-controls="missing-data"
        >
          Missing data
        </b-button>
      </b-col>
      <b-col cols="9">
        <ul id="missing-data" class="collapse" v-for="offer in missingData" :key="offer.id">
          <li>
            ID: <strong>{{ offer.itemId }}</strong> - Variant:
            <strong>{{ offer.variantKey }}</strong>
          </li>
        </ul>
      </b-col>
    </div>
    <b-row>
      <b-col><h1>Available Watches</h1></b-col>
    </b-row>
    <div v-if="status === localConstants.STATUS_LOADING">
      <b-row>
        <b-col class="text-center col-loading">
          <h3>Loading watches <b-spinner/></h3>
        </b-col>
      </b-row>
    </div>
    <div v-if="status === localConstants.STATUS_ERROR_LOADING">
      <b-row class="justify-content-center">
        <b-col cols="8" class="text-center col-loading">
          <div class="alert alert-danger">
            Temporary Error during loading. Please try to refresh the page and see if the error persists.
          </div>
        </b-col>
      </b-row>
    </div>
    <transition name="fade">
      <div v-if="status === localConstants.STATUS_FINISHED_LOADING">
        <b-row class="align-items-end">
          <b-col cols="12" md="3">
            <label for="textFilter">Text</label>
            <input
              type="text"
              v-model="textFilter"
              id="textFilter"
              class="form-control form-control-sm"
              placeholder="Search Brand, Model, Reference Number"
            />
          </b-col>
          <b-col cols="12" md="3">
            <label for="brandSelected">Brands</label>
            <select v-model="brandSelected" id="brandSelected" class="form-control form-control-sm select-brand">
              <option value=""></option>
              <option v-for="brandOption in brandOptions" :value="brandOption" :key="brandOption">
                {{ brandOption }}
              </option>
            </select>
          </b-col>
          <b-col cols="12" md="2" class="text-right">
            <br />
            <b-button variant="secondary" size="sm" block
              @click="resetFilter()"
              :class="{ 'btn-primary': filterActive }"
              :disabled="!filterActive"
            >
              Reset Filter <b-icon-arrow-counterclockwise/>
            </b-button>
          </b-col>
        </b-row>
        <b-row style="padding-top: 10px;">
          <b-col cols="12" class="text-center">
            <div class="btn btn-sm btn-outline-primary" v-if="!moreFiltersShown" @click="moreFiltersShown = true">
              <b-icon-chevron-down class="rounded-circle bg-primary" variant="light"/> More filters
            </div>
            <div class="btn btn-sm btn-outline-primary" v-if="moreFiltersShown" @click="moreFiltersShown = falsecss">
              <b-icon-chevron-up class="rounded-circle bg-primary" variant="light"/> Less filters
            </div>
          </b-col>
        </b-row>
        <div v-if="moreFiltersShown" class="more-filters-container" :class="{ closed: !moreFiltersShown }">
          <b-row class="filter-row">
            <b-col cols="6" md="3">
              <h3 class="filter-title">Condition</h3>
              <div class="form-check">
                <div v-for="(conditionOption, key) in conditionOptions" :key="key">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="conditionsSelected"
                    :value="conditionOption"
                    :id="'_condition_' + key"
                  />
                  <label class="form-check-label" :for="'_condition_' + key">
                    {{ conditionOptionReadable(conditionOption) }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col cols="6" md="3">
              <h3 class="filter-title">Years</h3>
              <div class="form-check">
                <div v-for="(yearGroupOption, key) in yearGroupOptions" :key="key">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="yearGroupsSelected"
                    :value="yearGroupOption"
                    :id="'_yearGroup_' + key"
                  />
                  <label class="form-check-label" :for="'_yearGroup_' + key">
                    {{ yearGroupOption }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="3">
              <h3 class="filter-title">Taxation</h3>
              <select v-model="taxTypeSelected" class="form-control form-control-sm select-taxType">
                <option value=""></option>
                <option v-for="taxTypeOption in taxTypeOptions" :value="taxTypeOption" :key="taxTypeOption">
                  {{ taxTypeOptionReadable(taxTypeOption) }}
                </option>
              </select>
            </b-col>
          </b-row>
        </div>
        <b-row>
          <b-col cols="12"><hr class="thin-hr" /></b-col>
          <b-col cols="12" sm="9">
            Showing <strong>{{ numberOfFilteredWatches }}</strong> of {{ numberOfTotalWatches }} watches.
          </b-col>
          <b-col cols="12" sm="3" class="text-right">
            <select v-model="sortingOptionSelected" class="form-control form-control-sm select-sorting-option">
              <option v-for="sortingOption in sortingOptions" :value="sortingOption.key" :key="sortingOption.key">
                {{ sortingOption.name }}
              </option>
            </select>
          </b-col>
        </b-row>
        <b-row>
          <b-col><hr class="thin-hr" /></b-col>
        </b-row>
        <!--
          Some extra padding, due to the col-bordered below, so the borders align with the heading
        -->
        <b-col cols="12">
          <b-row>
            <div v-if="filteredWatches.length === 0">
              <h3 class="no-watches">
                Sorry, no watches matching your criteria were found.
              </h3>
            </div>
            <b-col cols="6" md="3" lg="2" v-for="watch in filteredWatches" :key="watch.id" class="col-bordered">
              <watch-in-grid :watch="watch" btn-text="Reserve" type="watch"></watch-in-grid>
            </b-col>
          </b-row>
        </b-col>
      </div>
    </transition>
  </b-container>
</template>

<script>
import { hasAnyOfTheseRoles } from 'src/auth';
import WatchInGrid from 'Components/WatchInGrid';
import store from 'src/store/index';

export default {
  name: 'SellingOffer',
  components: {
    WatchInGrid,
  },
  data: () => {
    return {
      moreFiltersShown: false,
      status: '',
      brandSelected: '',
      taxTypeSelected: '',
      conditionsSelected: [],
      yearGroupsSelected: [],
      sortingOptionSelected: 'updated_at_desc',
      textFilter: '',
      watches: [],
      missingData: [],
      sortingOptions: [
        {
          key: 'updated_at_desc',
          name: 'Most recent first',
        },
        {
          key: 'price_asc',
          name: 'Lowest Price First',
        },
        {
          key: 'price_desc',
          name: 'Highest Price First',
        },
      ],
      localConstants: {
        STATUS_LOADING: 'loading',
        STATUS_ERROR_LOADING: 'errorLoading',
        STATUS_FINISHED_LOADING: 'finishedLoading',
      },
    };
  },
  computed: {
    filterActive() {
      return (
        this.textFilter !== '' ||
        this.brandSelected !== '' ||
        this.taxTypeSelected !== '' ||
        this.conditionsSelected.length !== 0 ||
        this.yearGroupsSelected.length !== 0
      );
    },
    numberOfTotalWatches() {
      return this.watches.length;
    },
    numberOfFilteredWatches() {
      return this.filteredWatches.length;
    },
    brandOptions() {
      let brands = [];
      this.watches.forEach((watch) => {
        if (!brands.includes(watch.brand)) {
          brands.push(watch.brand);
        }
      });
      return brands.sort();
    },
    taxTypeOptions() {
      let taxTypes = [];
      this.watches.forEach((watch) => {
        if (!taxTypes.includes(watch.taxType)) {
          taxTypes.push(watch.taxType);
        }
      });
      return taxTypes.sort();
    },
    conditionOptions() {
      let conditions = [];
      this.watches.forEach((watch) => {
        if (!conditions.includes(watch.condition)) {
          conditions.push(watch.condition);
        }
      });
      console.log(conditions);
      let sortingExceptions = {
        new: 1,
        aaa: 2,
        aa: 3,
      };
      return conditions.sort((a, b) => {
        if (sortingExceptions[a] && sortingExceptions[b]) {
          return sortingExceptions[a] - sortingExceptions[b];
        } else if (sortingExceptions[a]) {
          return -1;
        }
        return a.localeCompare(b);
      });
    },
    yearGroupOptions() {
      let yearGroups = [];
      this.watches.forEach((watch) => {
        if (!yearGroups.includes(watch.yearGroup)) {
          yearGroups.push(watch.yearGroup);
        }
      });
      yearGroups.sort();
      return yearGroups.reverse();
    },
    filteredWatches() {
      let filteredWatches = this.watches.filter((watch) => {
        // Single Select Filters
        let watchBrand = watch.brand.toLowerCase();
        let selectedBrand = this.brandSelected || '';
        if (watchBrand.indexOf(selectedBrand.toLowerCase()) === -1) {
          return false;
        }

        let selectedTaxType = this.taxTypeSelected.toLowerCase();
        let watchTaxType = watch.taxType.toLowerCase();
        if (watchTaxType.indexOf(selectedTaxType) === -1) {
          return false;
        }

        // Text filter for multiple data fields
        let watchModel = watch.model.toLowerCase();
        let watchReference = '';
        if (watch.reference !== null) {
          watchReference = watch.reference.toLowerCase();
        }
        let textFilter = this.textFilter.toLowerCase().trim();
        if (
          watchBrand.indexOf(textFilter) === -1 &&
          watchModel.indexOf(textFilter) === -1 &&
          watchReference.indexOf(textFilter) === -1
        ) {
          return false;
        }

        // Multi-Select Filters
        let selectedConditions = this.conditionsSelected;
        if (selectedConditions.length !== 0 && selectedConditions.indexOf(watch.condition) === -1) {
          return false;
        }

        let selectedYearGroups = this.yearGroupsSelected;
        if (selectedYearGroups.length !== 0 && selectedYearGroups.indexOf(watch.yearGroup) === -1) {
          return false;
        }

        return true;
      });

      // Sorting Options
      if (this.sortingOptionSelected === 'price_asc') {
        return filteredWatches.sort((a, b) => {
          return a.offerPrice - b.offerPrice;
        });
      }
      if (this.sortingOptionSelected === 'price_desc') {
        return filteredWatches.sort((a, b) => {
          return b.offerPrice - a.offerPrice;
        });
      }
      if (this.sortingOptionSelected === 'update_at_desc') {
        return filteredWatches.sort((a, b) => {
          return b.updatedAt - a.updatedAt;
        });
      }

      return filteredWatches;
    },
  },
  methods: {
    resetFilter() {
      this.brandSelected = '';
      this.yearGroupsSelected = [];
      this.conditionsSelected = [];
      this.textFilter = '';
      this.taxTypeSelected = '';
    },
    hasAnyOfTheseRoles(roles) {
      return hasAnyOfTheseRoles(roles);
    },
    taxTypeOptionReadable(taxType) {
      if (taxType === 'diff') {
        return 'Differential';
      }
      if (taxType === 'full') {
        return 'Full';
      }
      return taxType;
    },
    conditionOptionReadable(condition) {
      if (condition === 'vint') {
        return 'Vintage';
      }
      if (condition === 'new') {
        return 'New';
      }
      if (condition === 'aa' || condition === 'aaa') {
        return condition.toUpperCase();
      }
      return condition;
    },
  },
  mounted() {
    this.status = this.localConstants.STATUS_LOADING;
    store
      .dispatch('loadSellingActiveOffers')
      .then((response) => {
        this.status = this.localConstants.STATUS_FINISHED_LOADING;
        response.data.offers.forEach((offer) => {
          if (offer.brand !== null) {
            offer.yearGroup = yearGroupOfWatch(offer.year);

            // short-term hack until offferPrice is always mandatory
            if (offer.offerPrice === undefined) {
              offer.offerPrice = '';
            }
            this.watches.push(offer);
          } else {
            this.missingData.push(offer);
          }
        });
      })
      .catch(() => {
        this.status = this.localConstants.STATUS_ERROR_LOADING;
      });
  },
};

// The naming and spaces have been set like this
// e.g. (»1990 and before« instead of »pre 1990«)
// so the default sorting method will list them correcly
function yearGroupOfWatch(year) {
  // Using this, so at least the top
  // two groups (»this year« and »2010-last year«)
  // update themselves each year
  let todaysDate = new Date();
  let thisYear = todaysDate.getFullYear();
  let lastYear = thisYear - 1;
  if (year >= thisYear) {
    return thisYear;
  }
  if (year >= 2010) {
    return '2010-' + lastYear;
  }
  if (year >= 2000) {
    return '2000-2009';
  }
  if (year >= 1) {
    return '1999 and before';
  }
  return ' unknown';
}
</script>

<style lang="scss" scoped>
.thin-hr {
  margin-top: 5px;
  margin-bottom: 5px;
}

.no-watches {
  color: #999999;
}

.filter-row {
  margin-top: 12px;
  .form-check-label {
    min-width: 80%;
    color: #555555;
  }

  input[type='checkbox']:checked + label {
    color: #111111;
  }
}

.filter-title {
  width: 100%;
  padding-bottom: 4px;
  border-bottom: 1px dotted #dddddd;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2px;
}

.col-loading {
  padding: 30px 0;
}

.select-brand {
  width: 100%;
}

.col-bordered {
  padding: 0;
  border: 1px solid #cccccc;
  &:hover,
  &:focus,
  &:active {
    cursor: pointer;
    border-color: #777777;
    box-shadow: 0 0 10px 0 rgba(50, 50, 50, 0.5);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
